import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { DeleteConfirm } from "../DeleteConfirm.tsx";
import { ErrorBanner } from "../ErrorBanner.tsx";
import { Pagination } from "../Pagination.tsx";
import { useCommunity } from "../community.ts";
import type { ContentSnapshot, Listing } from "../types.ts";
import { ListingForm } from "./ListingForm.tsx";

const PAGE_SIZE = 25;

export function Listings() {
  const community = useCommunity();
  const [items, setItems] = useState<ContentSnapshot[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Listing | null>(null);
  const [deleting, setDeleting] = useState<ContentSnapshot | null>(null);

  async function openEdit(id: string) {
    setBusy(`load-${id}`);
    try {
      const full = await api<Listing>(
        `/admin/listings/${encodeURIComponent(id)}`,
      );
      setEditing(full);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      if (community) params.set("community_id", community);
      const data = await api<{ items: ContentSnapshot[]; total: number }>(
        `/admin/listings?${params.toString()}`,
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [offset, community]);

  // Reset to first page whenever the community scope changes.
  useEffect(() => {
    setOffset(0);
  }, [community]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleHide(id: string, hidden: boolean) {
    const action = hidden ? "unhide" : "hide";
    setBusy(`${id}-${action}`);
    try {
      await api(
        `/admin/content/listing/${encodeURIComponent(id)}/${action}`,
        { method: "POST" },
      );
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="view-head">
        <h2>Listings</h2>
        <span className="view-meta">
          User-authored marketplace posts across the selected scope.
        </span>
      </div>
      <div className="view-actions">
        <button
          type="button"
          className="luna-btn luna-btn-primary luna-btn-sm"
          onClick={() => setCreating(true)}
          disabled={!community}
          title={
            community
              ? "Add a new listing"
              : "Pick a community to add a listing"
          }
        >
          + New listing
        </button>
      </div>
      {!community && (
        <div className="community-required">
          Pick a community above to add a new listing.
        </div>
      )}
      {error && <ErrorBanner code={error} />}
      {loading && items.length === 0 ? (
        <div className="spinner-block">Loading listings…</div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="empty">
            <strong>No listings in this scope</strong>
            <span>Switch communities or come back when authors post.</span>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="luna-table luna-table-hover">
            <thead>
              <tr>
                <th>Listing</th>
                <th>Community</th>
                <th>Title</th>
                <th>Author</th>
                <th>Created</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id}>
                  <td>
                    <a
                      href={`#content/listing/${encodeURIComponent(l.id)}`}
                    >
                      #{l.id}
                    </a>
                  </td>
                  <td className="meta">#{l.communityId}</td>
                  <td>{l.title}</td>
                  <td className="meta">user #{l.authorUserId}</td>
                  <td className="meta">{l.createdAt}</td>
                  <td>
                    {l.hidden ? (
                      <span className="luna-badge luna-badge-error">
                        hidden
                      </span>
                    ) : (
                      <span className="luna-badge luna-badge-success">
                        live
                      </span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="luna-btn luna-btn-outline luna-btn-sm"
                      onClick={() => void toggleHide(l.id, l.hidden)}
                      disabled={busy !== null}
                    >
                      {busy === `${l.id}-${l.hidden ? "unhide" : "hide"}`
                        ? "…"
                        : l.hidden
                          ? "Unhide"
                          : "Hide"}
                    </button>
                    <button
                      type="button"
                      className="luna-btn-row-edit"
                      onClick={() => void openEdit(l.id)}
                      disabled={busy !== null}
                    >
                      {busy === `load-${l.id}` ? "…" : "Edit"}
                    </button>
                    <button
                      type="button"
                      className="luna-btn-row-delete"
                      onClick={() => setDeleting(l)}
                      disabled={busy !== null}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        total={total}
        limit={PAGE_SIZE}
        offset={offset}
        onChange={setOffset}
        loading={loading}
        label="Listings"
      />
      {creating && community && (
        <ListingForm
          communityId={community}
          initial={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
      {editing && (
        <ListingForm
          communityId={editing.communityId}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {deleting && (
        <DeleteConfirm
          title={`Delete listing #${deleting.id}`}
          description={`Delete the listing “${deleting.title}”?`}
          endpoint={`/admin/listings/${encodeURIComponent(deleting.id)}`}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}
    </>
  );
}
